import qs from 'qs';
import { ADMIN_ENDPOINT_SHORT, ENDPOINT, VEHICLES } from 'src/constants/api';
import COLUMNS from 'src/screens/inventory/hooks/columns/columnsNames';

import { baseApi } from '../base';
import { uploadImage } from './uploadImage';

type ExpireVehicleParams = { vehicleIds: number[]; sold?: boolean; finalPrice?: null | number };

type UpdateAlliancesParams = { vehicleIds: number[]; enable: boolean };
export type UpdateAlliancesFn = ({ vehicleIds, enable }: UpdateAlliancesParams) => Promise<any>;

type UpdateVehiclesParams = {
  vehicleIds: number[];
  data: { [key: string]: boolean | number };
};
export type UpdateVehiclesFn = ({ vehicleIds, data }: UpdateVehiclesParams) => Promise<any>;

type FetchVehicleStatsParams = { vehicleId: number; from: string; to: string };

type BulkDeleteVehicleImagesParams = { vehicleId: number; mediaIds: number[] };

export type BulkDeleteVehicleImagesResponse = {
  deleted_media_ids: number[];
  gallery: Media[];
};

export const BULK_IMAGE_DELETION_ERROR = {
  INVALID_SELECTION: 'INVALID_MEDIA_SELECTION',
  PROCESSING_ACTIVE: 'MEDIA_PROCESSING_ACTIVE',
} as const;

const inventoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInventory: builder.mutation({
      query: (filters: any) => {
        const params = {
          stats: true,
          costs: true,
          pricing_monthly: true,
          deals: true,
          ...filters,
          ...(!filters.per_page && { per_page: 50 }),
          ...(filters.sort && {
            sort: filters.sort === COLUMNS.AGE ? COLUMNS.RECEPTION_DATE : filters.sort,
          }),
        };
        return {
          url: `/vehicles?${qs.stringify(params)}`,
          method: 'GET',
        };
      },
    }),
    getFeaturedFilters: builder.query<any, {}>({
      query: () => {
        return `${VEHICLES}/metadata`;
      },
    }),
    getFilters: builder.query<any, {}>({
      query: (filters: any) => {
        const params = { ...filters, account_only: 1 };
        return `${VEHICLES}/filters?${qs.stringify(params)}`;
      },
    }),
    getVehicle: builder.query<any, {}>({
      query: (id: number) => {
        return `${ADMIN_ENDPOINT_SHORT.replace('/api/v1', '/api/v1/')}${VEHICLES}/${id}`;
      },
    }),
    expireVehicles: builder.mutation({
      query: ({ vehicleIds, sold, finalPrice }: ExpireVehicleParams) => ({
        url: `${VEHICLES}/bulk/action/expire?${qs.stringify({
          reason: sold ? 1 : 2,
          ...(finalPrice && { finalPrice }),
        })}`,
        method: 'POST',
        body: { vehicles: vehicleIds },
      }),
    }),
    updateVehicles: builder.mutation({
      query: ({ vehicleIds, data }: { vehicleIds: number[]; data: {} }) => ({
        url: `${VEHICLES}/bulk/update`,
        method: 'POST',
        body: {
          vehicles: vehicleIds,
          data,
        },
      }),
    }),
    updateInventoryVehicle: builder.mutation({
      query: ({ vehicleId, data }: { vehicleId: number | null; data: any }) => ({
        url: vehicleId ? `${VEHICLES}/${vehicleId}/update` : VEHICLES,
        method: vehicleId ? 'PATCH' : 'POST',
        body: vehicleId ? data : { ...data, allow_alliances: true },
      }),
    }),
    // TODO: remover por S3 upload
    uploadImage: builder.mutation({
      queryFn: uploadImage,
    }),
    getUploadMediaUrl: builder.mutation({
      query: ({ vehicleId, data }: { vehicleId: number; data: any }) => ({
        url: `${ENDPOINT}/${VEHICLES}/${vehicleId}/media/sign`,
        method: 'POST',
        body: data,
      }),
    }),
    commitMedia: builder.mutation({
      query: ({ vehicleId, data }: { vehicleId: number; data: any }) => ({
        url: `${ENDPOINT}/${VEHICLES}/${vehicleId}/media/commit`,
        method: 'POST',
        body: data,
      }),
    }),
    bulkDeleteVehicleImages: builder.mutation<
      BulkDeleteVehicleImagesResponse,
      BulkDeleteVehicleImagesParams
    >({
      query: ({ vehicleId, mediaIds }) => ({
        url: `${ENDPOINT}/${VEHICLES}/${vehicleId}/images`,
        method: 'DELETE',
        body: { media_ids: mediaIds },
      }),
      transformResponse: (response: any) => response?.data ?? response,
    }),
    updateVehicleStockPhotos: builder.mutation({
      query: (vehicleId: number) => ({
        url: `${VEHICLES}/${vehicleId}/import-stock-photos`,
        method: 'POST',
        headers: {
          'Idempotency-Key': crypto.randomUUID(),
        },
      }),
      transformResponse: (response: any) => response?.data ?? response,
    }),
    updateVehicleAcquisition: builder.mutation({
      query: ({ vehicleId, data }: { vehicleId: number; data: any }) => ({
        url: `${VEHICLES}/${vehicleId}/acquisition`,
        method: 'PATCH',
        body: data,
      }),
    }),
    updateVehicleLien: builder.mutation({
      query: ({ vehicleId, data }: { vehicleId: number; data: any }) => ({
        url: `${VEHICLES}/${vehicleId}/lien`,
        method: 'PATCH',
        body: data,
      }),
    }),
    updateAlliancesVisibility: builder.mutation({
      query: ({ vehicleIds, enable = true }: { vehicleIds: number[]; enable: boolean }) => ({
        url: `${VEHICLES}/bulk/alliances`,
        method: 'POST',
        body: { enable, vehicles: vehicleIds },
      }),
    }),
    fetchInventoryVehicleStats: builder.mutation({
      query: ({ vehicleId, from, to }: FetchVehicleStatsParams) => ({
        url: `${VEHICLES}/stats?vehicle_id=${vehicleId}&from=${from}&to=${to}`,
        method: 'GET',
      }),
    }),
    fetchVinInfo: builder.mutation({
      query: (vin: string) => ({
        url: `${VEHICLES}/vin/${vin}`,
        method: 'GET',
      }),
    }),
    fetchPricing: builder.mutation({
      query: ({ makeId, modelId, year }) => ({
        url: `${VEHICLES}/pricing/monthly/models?make_id=${makeId}&model_id=${modelId}&year=${year}&include_names=1`,
        method: 'GET',
      }),
    }),
    getColors: builder.query<{ id: number; name: string }[], void>({
      query: () => `${VEHICLES}/colors`,
      transformResponse: (response: any) => {
        const data = response?.data;
        return Array.isArray(data) ? data : (data?.data ?? []);
      },
    }),
    getTypes: builder.query<{ id: number; name: string }[], void>({
      query: () => `${VEHICLES}/types`,
      transformResponse: (response: any) => {
        const data = response?.data;
        return Array.isArray(data) ? data : (data?.data ?? []);
      },
    }),
    fetchBrands: builder.mutation({
      query: (year: number | undefined) => ({
        url: `${VEHICLES}/brands?${qs.stringify({ year })}`,
        method: 'GET',
      }),
    }),
    fetchModels: builder.mutation({
      query: ({ year, makeId }: { year: number | undefined; makeId: number }) => ({
        url: `${VEHICLES}/brands/${makeId}/models?${qs.stringify({ year })}`,
        method: 'GET',
      }),
    }),
    getAllYears: builder.mutation({
      query: () => ({
        url: `${VEHICLES}/years`,
        method: 'GET',
      }),
    }),
    getActiveYears: builder.mutation({
      query: () => ({
        url: `${VEHICLES}/active-years`,
        method: 'GET',
      }),
    }),
    getInstitutions: builder.mutation({
      query: (types?: string[]) => ({
        url: `institutions?types=${types?.join(',')}`,
        method: 'GET',
      }),
    }),
    getAccountVinList: builder.mutation({
      query: (filter?: string) => {
        // filter puede ser available-for-deals para no traer vin con proceso de venta abiertos
        const param = filter ? `filter=${filter}` : '';
        return {
          url: `${ADMIN_ENDPOINT_SHORT}/account/vehicles/vin?${param}`,
          method: 'GET',
        };
      },
    }),
  }),
});

export const {
  useGetInventoryMutation,
  useLazyGetFeaturedFiltersQuery,
  useLazyGetFiltersQuery,
  useLazyGetVehicleQuery,
  useExpireVehiclesMutation,
  useUpdateInventoryVehicleMutation,
  useUploadImageMutation,
  useGetUploadMediaUrlMutation,
  useCommitMediaMutation,
  useBulkDeleteVehicleImagesMutation,
  useUpdateVehicleStockPhotosMutation,
  useUpdateVehicleAcquisitionMutation,
  useUpdateVehicleLienMutation,
  useUpdateAlliancesVisibilityMutation,
  useFetchInventoryVehicleStatsMutation,
  useFetchVinInfoMutation,
  useFetchPricingMutation,
  useGetColorsQuery,
  useLazyGetColorsQuery,
  useGetTypesQuery,
  useLazyGetTypesQuery,
  useFetchBrandsMutation,
  useFetchModelsMutation,
  useGetAllYearsMutation,
  useGetActiveYearsMutation,
  useGetInstitutionsMutation,
  useGetAccountVinListMutation,
  useUpdateVehiclesMutation,
} = inventoryApi;
